/**
 * Safe Area Hook - iOS notch/Dynamic Island support
 * 
 * Provides safe area insets for proper content positioning
 * on devices with notches, Dynamic Islands, and home indicators.
 */

import { useState, useEffect } from 'react';
import { Capacitor } from '@capacitor/core';

interface SafeAreaInsets {
  top: number;
  bottom: number;
  left: number;
  right: number;
}

const DEFAULT_INSETS: SafeAreaInsets = {
  top: 0,
  bottom: 0,
  left: 0,
  right: 0,
};

// Standard iOS safe area values for common devices
const IOS_NOTCH_TOP = 47; // iPhone X and later
const IOS_DYNAMIC_ISLAND_TOP = 59; // iPhone 14 Pro and later
const IOS_HOME_INDICATOR_BOTTOM = 34;

/**
 * Hook to get safe area insets
 * Updates when device orientation or platform changes
 */
export function useSafeArea(): SafeAreaInsets {
  const [insets, setInsets] = useState<SafeAreaInsets>(DEFAULT_INSETS);

  useEffect(() => {
    const calculateInsets = () => {
      const isNative = Capacitor.isNativePlatform();
      const isIOS = Capacitor.getPlatform() === 'ios';

      if (!isNative) {
        // On web, use CSS env() values if available
        const computedStyle = getComputedStyle(document.documentElement);
        
        const parseEnvValue = (value: string): number => {
          const parsed = parseInt(value, 10);
          return isNaN(parsed) ? 0 : parsed;
        };

        setInsets({
          top: parseEnvValue(computedStyle.getPropertyValue('--sat') || '0'),
          bottom: parseEnvValue(computedStyle.getPropertyValue('--sab') || '0'),
          left: parseEnvValue(computedStyle.getPropertyValue('--sal') || '0'),
          right: parseEnvValue(computedStyle.getPropertyValue('--sar') || '0'),
        });
        return;
      }

      if (isIOS) {
        // Detect device type based on screen dimensions
        const screenHeight = window.screen.height;
        const screenWidth = window.screen.width;
        const maxDim = Math.max(screenHeight, screenWidth);
        const minDim = Math.min(screenHeight, screenWidth);
        
        // iPhone X and later have taller aspect ratios
        const aspectRatio = maxDim / minDim;
        const hasNotch = aspectRatio > 2.0;
        
        // iPhone 14 Pro and later have Dynamic Island (taller than iPhone X series)
        const hasDynamicIsland = maxDim >= 932;

        if (hasNotch || hasDynamicIsland) {
          setInsets({
            top: hasDynamicIsland ? IOS_DYNAMIC_ISLAND_TOP : IOS_NOTCH_TOP,
            bottom: IOS_HOME_INDICATOR_BOTTOM,
            left: 0,
            right: 0,
          });
        } else {
          // Older iPhones without notch
          setInsets({
            top: 20, // Status bar
            bottom: 0,
            left: 0,
            right: 0,
          });
        }
      } else {
        // Android native shell uses WindowCompat.setDecorFitsSystemWindows(true),
        // so the WebView is already laid out below the status bar. Adding a JS
        // top inset here creates the extra top nav padding seen on Android.
        setInsets({
          top: 0,
          bottom: 0,
          left: 0,
          right: 0,
        });
      }
    };

    calculateInsets();

    // Recalculate on resize (orientation change)
    window.addEventListener('resize', calculateInsets);
    window.addEventListener('orientationchange', calculateInsets);

    return () => {
      window.removeEventListener('resize', calculateInsets);
      window.removeEventListener('orientationchange', calculateInsets);
    };
  }, []);

  return insets;
}

/**
 * Get safe area CSS custom properties for inline styles
 */
export function getSafeAreaStyles(insets: SafeAreaInsets): React.CSSProperties {
  return {
    paddingTop: `max(${insets.top}px, env(safe-area-inset-top, 0px))`,
    paddingBottom: `max(${insets.bottom}px, env(safe-area-inset-bottom, 0px))`,
    paddingLeft: `max(${insets.left}px, env(safe-area-inset-left, 0px))`,
    paddingRight: `max(${insets.right}px, env(safe-area-inset-right, 0px))`,
  };
}

/**
 * Get just the top safe area padding (for headers)
 */
export function getTopSafeAreaStyle(insets: SafeAreaInsets): React.CSSProperties {
  return {
    paddingTop: `max(${insets.top}px, env(safe-area-inset-top, 0px))`,
  };
}

/**
 * Get just the bottom safe area padding (for footers/tab bars)
 */
export function getBottomSafeAreaStyle(insets: SafeAreaInsets): React.CSSProperties {
  return {
    paddingBottom: `max(${insets.bottom}px, env(safe-area-inset-bottom, 0px))`,
  };
}

/**
 * Check if device has a notch or Dynamic Island
 */
export function useHasNotch(): boolean {
  const [hasNotch, setHasNotch] = useState(false);

  useEffect(() => {
    const isIOS = Capacitor.getPlatform() === 'ios';
    if (!isIOS) {
      setHasNotch(false);
      return;
    }

    const screenHeight = window.screen.height;
    const screenWidth = window.screen.width;
    const maxDim = Math.max(screenHeight, screenWidth);
    const minDim = Math.min(screenHeight, screenWidth);
    const aspectRatio = maxDim / minDim;
    
    setHasNotch(aspectRatio > 2.0);
  }, []);

  return hasNotch;
}

export default useSafeArea;
